import { useEffect, useMemo, useState } from 'react';
import { getBeginnerWeapons, getBeginnerSpellsForClass } from '../../api/dnd';
import CharacterCard from '../../components/CharacterCard';
import { saveProgress, defaultProgress } from '../game/progress';
import { saveSlot } from '../game/saveSlots';

const TOTAL_POINTS = 10;

export default function BuildPage({ onStart, slotIndex }) {
  const [name, setName] = useState('Hero');
  const [cls, setCls]   = useState('Warrior');
  const [pts, setPts]   = useState({ str:0, agi:0, int:0 });
  const left = useMemo(()=> TOTAL_POINTS - (pts.str+pts.agi+pts.int), [pts]);

  const [weapons, setWeapons] = useState([]);
  const [weaponIndex, setWeaponIndex] = useState('');
  const [weaponDetail, setWeaponDetail] = useState(null);

  const [spells, setSpells] = useState([]);
  const [spellKey, setSpellKey] = useState('');

  useEffect(()=> {
    getBeginnerWeapons().then(ws=>{
      setWeapons(ws);
      const w = ws[0] || null;
      setWeaponIndex(w?.index || '');
      setWeaponDetail(w);
    });
  }, []);
  useEffect(()=> {
    setSpells([]); setSpellKey('');
    getBeginnerSpellsForClass(cls,3).then(setSpells);
  }, [cls]);
  useEffect(()=> {
    setWeaponDetail(weapons.find(x=>x.index===weaponIndex) || null);
  }, [weaponIndex, weapons]);

  const inc = k => setPts(p => (left<=0?p:{...p,[k]:p[k]+1}));
  const dec = k => setPts(p => ({...p,[k]:Math.max(0,p[k]-1)}));

  const canStart = left===0 && weaponDetail && spellKey;

  const startGame = ()=>{
    if (!canStart) return;
    const chosen = spells.find(s=>s.key===spellKey);
    const hero = {
      name, cls,
      str:5+pts.str, agi:5+pts.agi, int:5+pts.int,
      hp:100,
      weapon:{ name:weaponDetail.name, base:weaponDetail.base }, // ✅ ไม่มี dice
      skill: chosen ? { key:chosen.key, name:chosen.name, kind:chosen.kind, base:chosen.base, explain:chosen.explain } : null,
    };
    const progress = { ...defaultProgress };
    saveProgress(progress);
    saveSlot(slotIndex ?? 0, hero, progress);
    localStorage.setItem('hero', JSON.stringify(hero));
    onStart(hero);
  };

  return (
    <div className="page">
      <h1 style={{marginBottom:10}}>Customize Your Hero</h1>

      <div className="build-grid">
        <div className="build-left card">
          <label>Hero Name</label>
          <input value={name} onChange={e=>setName(e.target.value)} />

          <label>Class</label>
          <select value={cls} onChange={e=>setCls(e.target.value)}>
            <option>Warrior</option><option>Mage</option><option>Rogue</option>
          </select>
          
          <div style={{marginTop:8, display:'flex', flexDirection:'column', gap:6}}>
            <StatRow label="STR" desc="พลังโจมตี" value={pts.str} inc={()=>inc('str')} dec={()=>dec('str')} />
            <StatRow label="AGI" desc="ความเร็ว/การหลบหลีก" value={pts.agi} inc={()=>inc('agi')} dec={()=>dec('agi')} />
            <StatRow label="INT" desc="พลังเวท" value={pts.int} inc={()=>inc('int')} dec={()=>dec('int')} />
          </div>

          <div style={{marginTop:6,fontSize:13,color:'#9fb2ca'}}>
            Points Left: <b style={{color:left===0?'#fff':'#f59e0b'}}>{left}</b> / {TOTAL_POINTS}
          </div>

          <label style={{marginTop:10}}>Beginner Weapon</label>
          <select value={weaponIndex} onChange={e=>setWeaponIndex(e.target.value)}>
            {weapons.map(w => <option key={w.index} value={w.index}>{w.name} — Base {w.base}</option>)}
          </select>
          {weaponDetail && (
            <div className="weapon-detail">
              <b>{weaponDetail.name}</b><br/>
              Base Damage: {weaponDetail.base}<br/>
              <small className="muted">{weaponDetail.desc || 'Simple weapon'}</small>
            </div>
          )}

          <label style={{marginTop:10}}>Beginner Skills ({cls})</label>
          <div className="skill-grid">
            {spells.map(s=>(
              <label key={s.key} className={`skill-card ${spellKey===s.key?'active':''}`}>
                <input type="radio" name="skill" value={s.key} checked={spellKey===s.key} onChange={()=>setSpellKey(s.key)} style={{display:'none'}}/>
                <div className="sk-top">
                  <b>{s.name}</b>
                  <span className={`tag ${s.kind}`}>{s.kind.toUpperCase()}</span>
                </div>
                <div className="sk-body">
                  <div className="sk-line">Effect: {s.explain}</div>
                  {s.kind==='damage' && <div className="sk-line">Base Damage: <b>{s.base}</b></div>}
                  {s.kind==='heal'   && <div className="sk-line">Heal: <b>{s.base}</b></div>}
                  {s.kind==='buff'   && <div className="sk-line">Buff: +bonus 1 turn</div>}
                  <small className="muted">{s.desc}</small>
                </div>
              </label>
            ))}
          </div>

          <div className="row" style={{marginTop:12}}>
            <button className="btn primary" disabled={!canStart} onClick={startGame}>Enter Arena</button>
            {!canStart && <span style={{fontSize:12,color:'#9fb2ca'}}>ใช้แต้มให้ครบ + เลือกสกิล 1 อย่าง</span>}
          </div>
        </div>

        <div className="build-right">
          <CharacterCard
            side="player"
            name={name}
            cls={cls}
            hp={100} maxHp={100}
            img={`https://api.dicebear.com/9.x/adventurer/svg?seed=${encodeURIComponent(name)}`}
            weapon={weaponDetail ? { name:weaponDetail.name, base:weaponDetail.base } : { name:'Fists', base:4 }}
            auraColor="#60a5fa"
          />
          <div style={{marginTop:10,fontSize:13,opacity:.9}}>
            <b>Stats Preview</b><br/>
            STR: {5+pts.str} · AGI: {5+pts.agi} · INT: {5+pts.int} {/* ✅ ชื่อครบ 3 ค่าชัดเจน */}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatRow({ label, desc, value, inc, dec }) {
  return (
    <div className="pt" style={{
      display:'flex', flexDirection:'column',
      alignItems:'flex-start', gap:4, padding:'6px 0'
    }}>
      <div style={{display:'flex',justifyContent:'space-between',width:'100%'}}>
        <span><b>{label}</b> <span style={{opacity:.7,fontSize:12}}>({desc})</span></span>
        <div>
          <button className="btn ghost" onClick={dec} style={{width:32,height:32}}>−</button>
          <b style={{display:'inline-block',width:32,textAlign:'center'}}>{value}</b>
          <button className="btn ghost" onClick={inc} style={{width:32,height:32}}>＋</button>
        </div>
      </div>
    </div>
  );
}